// employeeApi.js - API utilities for employee management operations

import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';
import profileImageCache from '../../services/profileImageCache';

const API_BASE_URL = 'https://api.innoworx.ai/Stage';

/**
 * Function to get the authentication token.
 * This function dynamically fetches the ID Token from AWS Amplify's Auth module.
 * It's crucial that a user is logged in for this to return a valid token.
 */
const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    // The ID Token is typically what API Gateway expects for authorization.
    if (tokens && tokens.idToken) {
      // Check if token is expired
      const idToken = tokens.idToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (idToken.payload.exp <= currentTime) {
        console.error('ID Token has expired. User needs to re-authenticate.');
        return null;
      }

      console.log('Successfully retrieved valid ID token for employee API');
      return tokens.idToken.toString();
    } else {
      console.error(
        'No ID Token found in the current session. User may not be logged in.'
      );
      return null;
    }
  } catch (error) {
    console.error('Error fetching authentication session:', error);
    return null;
  }
};

/**
 * Generic function to make authenticated API requests.
 * @param {string} endpoint - The API endpoint relative to the BASE_URL.
 * @param {string} method - The HTTP method (e.g., 'GET', 'POST').
 * @param {Object} [body=null] - The request body for POST/PUT requests.
 * @returns {Promise<Response>} - The response object from the API.
 * @throws {Error} - Throws an error if the API request fails or returns a non-OK status.
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  const token = await getAuthToken();

  if (!token) {
    const error = new Error(
      'Authentication token is missing. Please ensure a user is logged in.'
    );
    error.code = 'AUTHENTICATION_FAILED';
    error.details =
      'No valid authentication token found. User may need to log in again.';
    throw error;
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const config = {
    method: method,
    headers: headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  try {
    console.log(`Making ${method} request to: ${API_BASE_URL}${endpoint}`);
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(
        `API Error: ${response.status} ${response.statusText} for ${endpoint}`,
        errorData
      );

      const error = new Error(
        `API Error: ${response.status} - ${errorData.body ? errorData.body.error : 'Unknown error or no error message from API'}`
      );
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'employeeApiError',
        `Employee API error: ${response.status} ${response.statusText} for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          status: response.status,
          statusText: response.statusText,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[employeeApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    return response;
  } catch (error) {
    if (error.code === 'AUTHENTICATION_FAILED') {
      throw error;
    }

    console.error(`Network or Fetch Error for ${endpoint}:`, error);
    error.endpoint = endpoint;
    error.method = method;
    
    // Log network/fetch errors to logging service
    loggingService.logError(
      'employeeApiError',
      `Employee API network/fetch error for ${endpoint}`,
      error,
      {
        endpoint,
        method,
        errorType: error.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[employeeApi] Failed to log error:', logError);
    });
    
    throw error;
  }
};

/**
 * API utility functions for Employee Management.
 */
export const employeeApi = {
  /**
   * Fetches all available guards.
   * GET /user/guards
   * @returns {Promise<Array>} Array of guard objects
   */
  fetchAvailableGuards: async () => {
    try {
      const response = await makeApiRequest('/user/guards');
      const data = await response.json();
      console.log('Successfully fetched available guards:', data);
      return data;
    } catch (error) {
      console.error('Error fetching available guards:', error);
      throw error;
    }
  },

  /**
   * Fetches all available supervisors.
   * GET /user/supervisors
   * @returns {Promise<Array>} Array of supervisor objects
   */
  fetchAvailableSupervisors: async () => {
    try {
      const response = await makeApiRequest('/user/supervisors');
      const data = await response.json();
      console.log('Successfully fetched available supervisors:', data);
      return data;
    } catch (error) {
      console.error('Error fetching available supervisors:', error);
      throw error;
    }
  },

  /**
   * Fetches profile photo URL for a specific user.
   * GET /user/profile-picture?user_id={userId}
   * @param {string} userId - The ID of the user
   * @returns {Promise<string>} Profile photo URL or S3 path
   */
  getProfilePhoto: async (userId) => {
    try {
      if (!userId) {
        throw new Error('User ID is required to fetch profile photo');
      }

      // Check cache first - if we have a cached S3 path, return it
      const cachedS3Path = profileImageCache.getS3Path(userId);
      if (cachedS3Path) {
        console.log(`✅ Using cached S3 path for user ${userId}`);
        return cachedS3Path;
      }

      // Check if we have a cached URL
      const cachedUrl = profileImageCache.get(userId);
      if (cachedUrl) {
        console.log(`✅ Using cached profile image URL for user ${userId}`);
        return cachedUrl;
      }

      const response = await makeApiRequest(`/user/profile-picture?user_id=${userId}`);
      const data = await response.json();
      
      // Get the image path or URL from response
      const imagePath = data.profile_image_url || data.image_path || data.profile_image_path || '';
      
      // Cache the result
      if (imagePath) {
        if (imagePath.startsWith('http')) {
          // It's already a URL, cache it directly
          profileImageCache.set(userId, imagePath);
          console.log(`💾 Cached direct profile image URL for user ${userId}`);
        } else {
          // It's an S3 path, cache the path
          profileImageCache.setS3Path(userId, imagePath);
          console.log(`💾 Cached S3 path for user ${userId}`);
        }
      }
      
      // Return the profile image URL directly (matching Flutter implementation)
      return imagePath;
    } catch (error) {
      // Only log as warning to reduce console noise - this is expected for users without photos
      console.warn(`Profile photo not available for user ${userId}:`, error.message || error);
      // Return empty string if profile photo fetch fails
      return '';
    }
  },

  /**
   * Checks if a user is already assigned to a shift.
   * POST /shift/user-availability
   * @param {Object} params - Parameters for checking assignment
   * @param {string} params.userId - The ID of the user
   * @param {string} params.date - The date in YYYY-MM-DD format
   * @param {string} params.startTime - The start time
   * @param {string} params.endTime - The end time
   * @returns {Promise<boolean>} True if user is assigned, false otherwise
   */
  isUserAssignedToShift: async ({ userId, date, startTime, endTime }) => {
    try {
      if (!userId || !date || !startTime || !endTime) {
        throw new Error('All parameters (userId, date, startTime, endTime) are required');
      }

      const token = await getAuthToken();
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const requestBody = {
        user_id: userId,
        date: date,
        start_time: startTime,
        end_time: endTime
      };

      const response = await fetch(`${API_BASE_URL}/shift/user-availability`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (response.status === 200) {
        // Parse the boolean response from the API (matches Flutter implementation)
        const result = await response.json();
        return result === true; // Return true if user is assigned, false otherwise
      } else {
        const errorText = await response.text();
        const error = new Error(`Failed to check user shift availability: ${response.status} - ${errorText}`);
        error.status = response.status;
        error.statusText = response.statusText;
        error.endpoint = '/shift/user-availability';
        error.responseData = errorText;
        
        // Log API error to logging service
        loggingService.logError(
          'employeeApiError',
          `Employee API error: ${response.status} ${response.statusText} for /shift/user-availability`,
          error,
          {
            endpoint: '/shift/user-availability',
            method: 'POST',
            status: response.status,
            statusText: response.statusText,
            userId,
            date,
            startTime,
            endTime,
            responseData: errorText,
          }
        ).catch(logError => {
          console.warn('[employeeApi] Failed to log error:', logError);
        });
        
        throw error;
      }
    } catch (error) {
      console.error('Error checking user assignment:', error);
      
      // Log error to logging service (even though we return false)
      loggingService.logError(
        'employeeApiError',
        `Error checking user shift availability: ${error.message}`,
        error,
        {
          endpoint: '/shift/user-availability',
          method: 'POST',
          userId,
          date,
          startTime,
          endTime,
          errorType: error.name || 'Error',
        }
      ).catch(logError => {
        console.warn('[employeeApi] Failed to log error:', logError);
      });
      
      // Return false on error to allow assignment to proceed (matches Flutter behavior)
      return false;
    }
  },

  /**
   * Checks if a user is on leave for a specific date.
   * GET /leave/availability
   * @param {Object} params - Parameters for checking leave availability
   * @param {string} params.userId - The ID of the user
   * @param {string} params.date - The date in YYYY-MM-DD format
   * @param {number} params.retryCount - Number of retries attempted (internal use)
   * @returns {Promise<Object>} Object containing availability status
   */
  checkLeaveAvailability: async ({ userId, date, retryCount = 0 }) => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 1000; // 1 second delay between retries

    try {
      // Validate date format (YYYY-MM-DD) - matches Flutter validation
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(date)) {
        throw new Error('Date must be in YYYY-MM-DD format');
      }

      // Validate userId is not empty - matches Flutter validation
      if (!userId || userId.trim() === '') {
        throw new Error('User ID cannot be empty');
      }

      const token = await getAuthToken();
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const queryParams = new URLSearchParams({
        user_id: userId,
        date: date
      });

      const response = await fetch(`${API_BASE_URL}/leave/availability?${queryParams}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 200) {
        const data = await response.json();
        return data; // Return the parsed JSON response directly (matches Flutter)
      } else if (response.status === 400) {
        const body = await response.json();
        const errorMsg = body.error || 'Bad request';
        throw new Error(`Bad request: ${errorMsg}`);
      } else if (response.status === 401) {
        throw new Error('Unauthorized: Authentication required');
      } else if (response.status === 500) {
        const body = await response.json();
        const errorMsg = body.error || 'Internal server error';
        throw new Error(`Server error: ${errorMsg}`);
      } else {
        const responseText = await response.text();
        const error = new Error(`Failed to check leave availability: ${response.status} - ${responseText}`);
        error.status = response.status;
        error.statusText = response.statusText;
        error.endpoint = '/leave/availability';
        error.responseData = responseText;
        
        // Log API error to logging service
        loggingService.logError(
          'employeeApiError',
          `Employee API error: ${response.status} ${response.statusText} for /leave/availability`,
          error,
          {
            endpoint: '/leave/availability',
            method: 'GET',
            status: response.status,
            statusText: response.statusText,
            userId,
            date,
            responseData: responseText,
          }
        ).catch(logError => {
          console.warn('[employeeApi] Failed to log error:', logError);
        });
        
        throw error;
      }
    } catch (error) {
      console.error(`Error checking leave availability (attempt ${retryCount + 1}/${MAX_RETRIES + 1}):`, error);
      
      // Log error to logging service (only on final attempt or non-retryable errors)
      if (retryCount >= MAX_RETRIES || !(
        error.message.includes('Failed to fetch') || 
        error.message.includes('NetworkError') ||
        error.message.includes('Server error') ||
        error.message.includes('timeout')
      )) {
        loggingService.logError(
          'employeeApiError',
          `Error checking leave availability: ${error.message}`,
          error,
          {
            endpoint: '/leave/availability',
            method: 'GET',
            userId,
            date,
            retryCount,
            errorType: error.name || 'Error',
          }
        ).catch(logError => {
          console.warn('[employeeApi] Failed to log error:', logError);
        });
      }
      
      // Retry logic for network errors or server errors (5xx)
      if (retryCount < MAX_RETRIES && (
        error.message.includes('Failed to fetch') || 
        error.message.includes('NetworkError') ||
        error.message.includes('Server error') ||
        error.message.includes('timeout')
      )) {
        console.log(`Retrying leave availability check in ${RETRY_DELAY}ms...`);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return employeeApi.checkLeaveAvailability({ 
          userId, 
          date, 
          retryCount: retryCount + 1 
        });
      }
      
      // If max retries reached or non-retryable error, throw the error
      console.error(`Max retries (${MAX_RETRIES}) reached or non-retryable error for leave availability check`);
      throw error;
    }
  },

  /**
   * Fetches all users with their basic information.
   * GET /user/getall
   * @returns {Promise<Array>} Array of user objects
   */
  getAllUsers: async () => {
    try {
      const response = await makeApiRequest('/user/getall');
      const data = await response.json();
      console.log('Successfully fetched all users:', data);
      return data;
    } catch (error) {
      console.error('Error fetching all users:', error);
      throw error;
    }
  },

  /**
   * Fetches users by role (guards, supervisors, etc.).
   * GET /user/by-role?role={role}
   * @param {string} role - The role to filter by (e.g., 'guard', 'supervisor')
   * @returns {Promise<Array>} Array of user objects with the specified role
   */
  getUsersByRole: async (role) => {
    try {
      if (!role) {
        throw new Error('Role is required to fetch users by role');
      }

      const response = await makeApiRequest(`/user/by-role?role=${role}`);
      const data = await response.json();
      console.log(`Successfully fetched users with role ${role}:`, data);
      return data;
    } catch (error) {
      console.error(`Error fetching users with role ${role}:`, error);
      throw error;
    }
  },

  /**
   * Fetches all zones for a specific site.
   * GET /site/zone/getall-by-site?site_id={siteId}
   * @param {string} siteId - The ID of the site
   * @returns {Promise<Array>} Array of zone objects
   */
  getZonesBySiteId: async (siteId) => {
    try {
      if (!siteId) {
        throw new Error('Site ID is required to fetch zones');
      }

      const response = await makeApiRequest(`/site/zone/getall-by-site?site_id=${siteId}`);
      const data = await response.json();
      console.log(`Successfully fetched zones for site ${siteId}:`, data);
      return data;
    } catch (error) {
      console.error(`Error fetching zones for site ${siteId}:`, error);
      throw error;
    }
  },

  /**
   * Creates a new zone for a site.
   * POST /site/zone/create
   * @param {Object} zoneData - Zone creation data
   * @param {string} zoneData.site_id - The ID of the site
   * @param {string} zoneData.zone_name - The name of the zone
   * @param {string} [zoneData.description] - Optional description
   * @returns {Promise<Object>} Created zone object
   */
  createZone: async (zoneData) => {
    try {
      if (!zoneData.site_id || !zoneData.zone_name) {
        throw new Error('Site ID and zone name are required to create a zone');
      }

      const response = await makeApiRequest('/site/zone/create', 'POST', zoneData);
      const data = await response.json();
      console.log('Successfully created zone:', data);
      return data;
    } catch (error) {
      console.error('Error creating zone:', error);
      throw error;
    }
  },

  /**
   * Updates an existing zone.
   * PUT /site/zone/update
   * @param {Object} zoneData - Zone update data
   * @param {string} zoneData.zone_id - The ID of the zone
   * @param {string} [zoneData.zone_name] - Updated zone name
   * @param {string} [zoneData.description] - Updated description
   * @returns {Promise<Object>} Updated zone object
   */
  updateZone: async (zoneData) => {
    try {
      if (!zoneData.zone_id) {
        throw new Error('Zone ID is required to update a zone');
      }

      const response = await makeApiRequest('/site/zone/update', 'PUT', zoneData);
      const data = await response.json();
      console.log('Successfully updated zone:', data);
      return data;
    } catch (error) {
      console.error('Error updating zone:', error);
      throw error;
    }
  },

  /**
   * Deletes a zone.
   * DELETE /site/zone/delete?zone_id={zoneId}
   * @param {string} zoneId - The ID of the zone to delete
   * @returns {Promise<Object>} Deletion confirmation
   */
  deleteZone: async (zoneId) => {
    try {
      if (!zoneId) {
        throw new Error('Zone ID is required to delete a zone');
      }

      const response = await makeApiRequest(`/site/zone/delete?zone_id=${zoneId}`, 'DELETE');
      const data = await response.json();
      console.log('Successfully deleted zone:', data);
      return data;
    } catch (error) {
      console.error('Error deleting zone:', error);
      throw error;
    }
  },

};

export default employeeApi;
